import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import API from "../../api";
import toastr from "toastr";

interface UserType {
  id?: number;
  user_type_name: string;
  status: string;
  dashboard?: number;
  users?: number;
  adduser?: number;
  edituser?: number;
  deleteuser?: number;
  manage_user_permission?: number;
  patients?: number;
  addpatient?: number;
  editpatient?: number;
  deletepatient?: number;
  investigation?: number;
  doctor?: number;
  add_doctor?: number;
  edit_doctor?: number;
  delete_doctor?: number;
  billing?: number;
  report?: number;
  view_report?: number;
  delete_report?: number;
  payments?: number;
  finance?: number;
  income?: number;
  expense?: number;
  finance_report?: number;
  test_settings?: number;
}

interface UserTypeModalProps {
  show: boolean;
  onClose: () => void;
  mode: "add" | "edit";
  userTypeData?: UserType | null;
  onSuccess: () => void;
}

const ALL_PERMISSION_KEYS = [
  "dashboard",
  "users", "adduser", "edituser", "deleteuser", "manage_user_permission",
  "patients", "addpatient", "editpatient", "deletepatient",
  "report", "view_report", "delete_report",
  "payments", "finance", "income", "expense", "finance_report",
  "investigation",
  "doctor", "add_doctor", "edit_doctor", "delete_doctor",
  "billing", "test_settings",
];

const buildDefaultPermissions = (): Record<string, boolean> =>
  Object.fromEntries(ALL_PERMISSION_KEYS.map((k) => [k, false]));

const UserTypeModal: React.FC<UserTypeModalProps> = ({
  show,
  onClose,
  mode,
  userTypeData,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<UserType>({
    user_type_name: "",
    status: "1",
  });

  const [permissions, setPermissions] = useState<Record<string, boolean>>(
    buildDefaultPermissions()
  );

  const [loading, setLoading] = useState(false);

  // ── Load data for edit ───────────────────────────────────────────────────────
  useEffect(() => {
    if (mode === "edit" && userTypeData) {
      setFormData({
        id:             userTypeData.id,
        user_type_name: userTypeData.user_type_name,
        status:         userTypeData.status,
      });
      const updated: Record<string, boolean> = buildDefaultPermissions();
      ALL_PERMISSION_KEYS.forEach((key) => {
        updated[key] = (userTypeData as any)[key] === 1;
      });
      setPermissions(updated);
    } else {
      setFormData({ user_type_name: "", status: "1" });
      setPermissions(buildDefaultPermissions());
    }
  }, [mode, userTypeData]);

  // ── Input change ─────────────────────────────────────────────────────────────
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ── Toggle single permission ─────────────────────────────────────────────────
  const togglePermission = (key: string) => {
    setPermissions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // ── Toggle all permissions in a section ──────────────────────────────────────
  const toggleSection = (keys: string[]) => {
    const allOn = keys.every((k) => permissions[k]);
    setPermissions((prev) => {
      const updated = { ...prev };
      keys.forEach((k) => (updated[k] = !allOn));
      return updated;
    });
  };

  // ── Save ─────────────────────────────────────────────────────────────────────
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.user_type_name) {
      toastr.warning("User Type Name is required");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        ...Object.fromEntries(
          Object.entries(permissions).map(([k, v]) => [k, v ? 1 : 0])
        ),
      };

      if (mode === "add") {
        await API.post("/usertype/createUserType", payload);
        toastr.success("User Type added successfully!");
      } else if (mode === "edit" && formData.id) {
        await API.put(`/usertype/updateUserType/${formData.id}`, payload);
        toastr.success("User Type updated successfully!");
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error(error);
      toastr.error(error.response?.data?.message || "Failed to save user type");
    } finally {
      setLoading(false);
    }
  };

  // ── Section renderer (matches UserPermissionModal style) ─────────────────────
  const renderSection = (
    title: string,
    icon: string,
    color: string,
    items: { key: string; label: string }[]
  ) => {
    const sectionKeys = items.map((i) => i.key);
    const allOn  = sectionKeys.every((k) => permissions[k]);
    const someOn = sectionKeys.some((k) => permissions[k]);

    return (
      <div className={`card border-${color} mb-3`} key={title}>
        <div className="card-header bg-transparent py-2 d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <i className={`fas ${icon} text-${color} me-2`}></i>
            <h6 className={`mb-0 text-${color} fw-bold`}>{title}</h6>
          </div>
          <button
            type="button"
            className={`btn btn-sm ${
              allOn
                ? `btn-${color}`
                : someOn
                ? `btn-outline-${color}`
                : "btn-outline-secondary"
            }`}
            style={{ fontSize: "0.7rem", padding: "2px 8px" }}
            onClick={() => toggleSection(sectionKeys)}
          >
            {allOn ? "Deselect All" : "Select All"}
          </button>
        </div>
        <div className="card-body py-2 px-3">
          {items.map((perm) => (
            <div
              key={perm.key}
              className="d-flex align-items-center justify-content-between py-2"
              style={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}
            >
              <label
                htmlFor={`ut_${perm.key}`}
                className="fw-medium mb-0"
                style={{ cursor: "pointer", fontSize: "0.9rem" }}
              >
                {perm.label}
              </label>
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id={`ut_${perm.key}`}
                  checked={permissions[perm.key] ?? false}
                  onChange={() => togglePermission(perm.key)}
                  style={{ width: "2.5em", height: "1.25em", cursor: "pointer" }}
                />
                <label
                  className="form-check-label ms-1"
                  htmlFor={`ut_${perm.key}`}
                  style={{ cursor: "pointer" }}
                >
                  <span
                    className={`badge ${
                      permissions[perm.key] ? "bg-success" : "bg-secondary"
                    }`}
                  >
                    {permissions[perm.key] ? "ON" : "OFF"}
                  </span>
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Form onSubmit={handleSave}>
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-md modal-dialog-scrollable">
            <div className="modal-content">

              {/* ── Header ── */}
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  {mode === "add" ? "Add User Type" : "Edit User Type"}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={onClose}
                  aria-label="Close"
                />
              </div>

              {/* ── Body ── */}
              <Modal.Body
                className="bg-light"
                style={{ maxHeight: "65vh", overflowY: "auto" }}
              >
                {/* Name & Status */}
                <Row className="mb-2">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>User Type Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="user_type_name"
                        value={formData.user_type_name}
                        onChange={handleChange}
                        placeholder="Enter user type name"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Status</Form.Label>
                      <select
                        className="form-control"
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                      >
                        <option value="">Select Status</option>
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                      </select>
                    </Form.Group>
                  </Col>
                </Row>

                {/* ── Permission Sections ── */}
                {renderSection("Dashboard", "fa-tachometer-alt", "primary", [
                  { key: "dashboard", label: "Dashboard Access" },
                ])}

                {renderSection("Users", "fa-users", "success", [
                  { key: "users",                  label: "View Users" },
                  { key: "adduser",                label: "Add User" },
                  { key: "edituser",               label: "Edit User" },
                  { key: "deleteuser",             label: "Delete User" },
                  { key: "manage_user_permission", label: "Manage User Permission" },
                ])}

                {renderSection("Patients", "fa-user-injured", "info", [
                  { key: "patients",     label: "View Patients" },
                  { key: "addpatient",   label: "Add Patient" },
                  { key: "editpatient",  label: "Edit Patient" },
                  { key: "deletepatient",label: "Delete Patient" },
                ])}

                {renderSection("Doctor", "fa-user-md", "dark", [
                  { key: "doctor",       label: "View Doctors" },
                  { key: "add_doctor",   label: "Add Doctor" },
                  { key: "edit_doctor",  label: "Edit Doctor" },
                  { key: "delete_doctor",label: "Delete Doctor" },
                ])}

                {renderSection("Report", "fa-file-medical", "warning", [
                  { key: "report",        label: "Report Access" },
                  { key: "view_report",   label: "View Report" },
                  { key: "delete_report", label: "Delete Report" },
                ])}

                {renderSection("Finance", "fa-wallet", "danger", [
                  { key: "payments",       label: "Payments" },
                  { key: "finance",        label: "Finance" },
                  { key: "income",         label: "Income" },
                  { key: "expense",        label: "Expense" },
                  { key: "finance_report", label: "Finance Report" },
                ])}

                {renderSection("Other", "fa-th-large", "secondary", [
                  { key: "investigation", label: "Investigation" },
                  { key: "billing",       label: "Billing" },
                  { key: "test_settings", label: "Test Settings" },
                ])}
              </Modal.Body>

              {/* ── Footer ── */}
              <Modal.Footer className="border-top">
                <Button variant="outline-secondary" onClick={onClose} disabled={loading}>
                  <i className="fas fa-times me-1" /> Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-1"
                        role="status"
                        aria-hidden="true"
                      />
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-1" /> Save
                    </>
                  )}
                </Button>
              </Modal.Footer>

            </div>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default UserTypeModal;